import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useState } from "react";
import { Link } from "react-router";
import { Async, Empty, PageHead, Pager, SearchBox, Segmented } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useDebounced, usePaged } from "@/lib/hooks";

export function AuditPage() {
	const t = useT();
	const { locale } = useI18n();
	const [q, setQ] = useState("");
	const [scope, setScope] = useState<"" | "admin." | "page.">("");
	const [open, setOpen] = useState<number | null>(null);
	const query = useDebounced(q);
	const { state, rows, hasMore, loadingMore, more } = usePaged(
		(offset, limit) => api.audit({ q: query, action: scope, limit, offset }),
		(page) => page.entries,
		[query, scope],
		100,
	);

	return (
		<>
			<PageHead title={t("audit.title")} lede={t("audit.lede")} />
			<div className="table-tools">
				<SearchBox value={q} onChange={setQ} />
				<Segmented
					value={scope}
					onChange={setScope}
					options={[
						{ value: "", label: t("state.all") },
						{ value: "admin.", label: t("audit.filter.console") },
						{ value: "page.", label: t("audit.filter.panel") },
					]}
				/>
			</div>
			<Async state={state}>
				{() =>
					rows.length === 0 ? (
						<Empty />
					) : (
						<>
							<div className="table-scroll">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("audit.when")}</TableHead>
											<TableHead>{t("audit.who")}</TableHead>
											<TableHead>{t("audit.action")}</TableHead>
											<TableHead>{t("audit.target")}</TableHead>
											<TableHead>{t("audit.org")}</TableHead>
											<TableHead>{t("audit.detail")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{rows.map((e) => (
											<TableRow key={e.id}>
												<TableCell className="small">{fmt.dateTime(locale, e.created_at)}</TableCell>
												<TableCell>
													{e.actor_user_id ? (
														<Link to={`/users/${e.actor_user_id}`}>{e.actor_email ?? e.actor_user_id}</Link>
													) : (
														"—"
													)}
												</TableCell>
												<TableCell className="mono">{e.action}</TableCell>
												<TableCell className="mono small">{e.target ?? ""}</TableCell>
												<TableCell>
													{e.org_id ? <Link to={`/orgs/${e.org_id}`}>{e.org_name ?? e.org_id}</Link> : ""}
												</TableCell>
												<TableCell>
													{e.detail ? (
														open === e.id ? (
															<>
																<pre>{JSON.stringify(e.detail, null, 2)}</pre>
																<Button variant="ghost" type="button" onClick={() => setOpen(null)}>
																	{t("state.close")}
																</Button>
															</>
														) : (
															<Button variant="ghost" type="button" onClick={() => setOpen(e.id)}>
																{t("state.open")}
															</Button>
														)
													) : null}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<Pager shown={rows.length} hasMore={hasMore} busy={loadingMore} onMore={more} />
						</>
					)
				}
			</Async>
		</>
	);
}
